/**
 * Video upload contract (P-COND-F4B29EA85EDC) — frontend MIRROR of the backend
 * SSoT `backend/app/core/screen_recording.py` (`VIDEO_MIME_TYPES`,
 * `VIDEO_EXTENSIONS`, `MAX_VIDEO_UPLOAD_BYTES`, `MAX_VIDEO_DURATION_SEC`).
 * Change the backend first; `__tests__/video.parity.test.ts` parses the Python
 * source and fails on any drift.
 */

/** Accepted video MIME types (backend `VIDEO_MIME_TYPES`). */
export const VIDEO_MIME_TYPES: ReadonlySet<string> = new Set([
  'video/mp4', 'video/quicktime', 'video/webm',
]);

/** Accepted video extensions, with the dot (backend `VIDEO_EXTENSIONS`). */
export const VIDEO_EXTENSIONS: readonly string[] = ['.mp4', '.mov', '.webm'];

/** Per-video upload cap: 40 MiB (backend `MAX_VIDEO_UPLOAD_BYTES`). */
export const MAX_VIDEO_UPLOAD_BYTES = 40 * 1024 * 1024;

/** Longest video the backend will extract frames from (backend `MAX_VIDEO_DURATION_SEC`). */
export const MAX_VIDEO_DURATION_SEC = 180;

/** Upload request timeout when the batch contains a video (default uploads use 120 s). */
export const VIDEO_UPLOAD_TIMEOUT_MS = 300000;

/** Extension -> MIME, used to re-type preview blobs served as octet-stream. */
export const VIDEO_MIME_BY_EXT: Readonly<Record<string, string>> = {
  '.mp4': 'video/mp4', '.mov': 'video/quicktime', '.webm': 'video/webm',
};

function dotExt(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
}

/** True for an accepted video by MIME (parameters ignored) or by extension. */
export function isVideoFile(name: string, mimeType = ''): boolean {
  const mime = mimeType.split(';')[0].trim().toLowerCase();
  return VIDEO_MIME_TYPES.has(mime) || VIDEO_EXTENSIONS.includes(dotExt(name));
}

/** Video MIME for a preview blob: the file's own video type, else by extension. */
export function videoMimeType(name: string, mimeType = ''): string {
  const mime = mimeType.split(';')[0].trim().toLowerCase();
  if (VIDEO_MIME_TYPES.has(mime)) return mime;
  return VIDEO_MIME_BY_EXT[dotExt(name)] || 'video/mp4';
}

/** Seconds -> "m:ss" (design chip sub-line, e.g. "0:42"). */
export function formatVideoDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
